import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react";
import * as React from "react";
import { DayPicker, getDefaultClassNames } from "react-day-picker";

import { format } from "date-fns";
import { enUS, it } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { Button, buttonVariants } from "~/components/ui/Button";
import { cn } from "~/lib/utils";

const dateFnsLocales: Record<string, Locale> = { it, en: enUS };

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"];
  selected?: Date;
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  formatters,
  components,
  ...props
}: CalendarProps) {
  const { i18n, t } = useTranslation();
  const currentLocale = dateFnsLocales[i18n.language] || it;
  const defaultClassNames = getDefaultClassNames();
  // Mese di default: quando il picker lavora in UTC (`timeZone="UTC"`, come nel
  // booking), react-day-picker interpreta `defaultMonth` con `startOfMonth` nel
  // fuso UTC. Se lo calcolassimo come mezzanotte LOCALE, per i fusi avanti
  // rispetto a UTC (Italia CEST = UTC+2) la mezzanotte del giorno 1 cadrebbe nel
  // mese precedente in UTC (es. 1 giugno 00:00 CEST = 31 maggio 22:00 UTC) e il
  // calendario aprirebbe sul mese sbagliato. Costruendolo dai componenti UTC di
  // "adesso" l'istante cade sempre dentro il mese corretto anche in UTC.
  // Per gli altri usi (ora locale) manteniamo il comportamento precedente.
  const now = new Date();
  const today =
    props.timeZone === "UTC"
      ? new Date(
          Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
        )
      : (() => {
          const d = new Date();
          d.setHours(0, 0, 0, 0);
          return d;
        })();
  const selected = props.selected;
  const selectedDate =
    selected && typeof selected === "object" && "getTime" in selected
      ? (selected as Date)
      : undefined;
  const defaultMonth: Date =
    selectedDate && !isNaN(selectedDate.getTime()) ? selectedDate : today;

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      defaultMonth={defaultMonth}
      className={cn(
        "bg-background group/calendar p-3 [--cell-size:2rem] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      captionLayout={captionLayout}
      locale={currentLocale}
      labels={{
        labelDayButton: (date, { today, selected }) => {
          if (!date || isNaN(date.getTime())) {
            return "";
          }
          let label = format(date, "PPPP", { locale: currentLocale });
          if (today) label = `${t("Oggi")}, ${label}`;
          if (selected) label = `${label}, ${t("selezionato")}`;
          return label;
        },
        labelWeekNumber: (weekNumber) => `${t("Settimana")} ${weekNumber}`,
        labelNext: () => t("Prossimo mese"),
        labelPrevious: () => t("Mese precedente"),
        labelMonthDropdown: () => t("Seleziona il mese"),
        labelYearDropdown: () => t("Seleziona l'anno"),
      }}
      formatters={{
        formatCaption: (date) => {
          if (!date || isNaN(date.getTime())) {
            return "";
          }
          return format(date, "LLLL y", { locale: currentLocale });
        },
        formatMonthDropdown: (date) => {
          if (!date || isNaN(date.getTime())) {
            return "";
          }
          return format(date, "LLL", { locale: currentLocale });
        },
        ...formatters,
      }}
      classNames={{
        root: cn(
          "w-full lg:min-w-[480px] max-w-[480px]",
          defaultClassNames.root
        ),
        months: cn(
          "relative flex flex-col gap-4 md:flex-row",
          defaultClassNames.months
        ),
        month: cn(
          "flex w-full flex-col gap-4 uppercase",
          defaultClassNames.month
        ),
        nav: cn(
          "absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1",
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "h-[--cell-size] w-[--cell-size] select-none p-0 aria-disabled:opacity-50",
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "h-[--cell-size] w-[--cell-size] select-none p-0 aria-disabled:opacity-50",
          defaultClassNames.button_next
        ),
        month_caption: cn(
          "flex h-[--cell-size] w-full items-center justify-center px-[--cell-size]",
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          "flex h-[--cell-size] w-full items-center justify-center gap-1.5 text-sm font-medium",
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          "has-focus:border-ring border-input shadow-xs has-focus:ring-ring/50 has-focus:ring-[3px] relative rounded-full border",
          defaultClassNames.dropdown_root
        ),
        dropdown: cn(
          "bg-popover absolute inset-0 opacity-0",
          defaultClassNames.dropdown
        ),
        caption_label: cn(
          "select-none font-medium",
          captionLayout === "label"
            ? "text-lg"
            : "[&>svg]:text-muted-foreground flex h-8 items-center gap-1 rounded-full pl-2 pr-1 text-sm [&>svg]:size-3.5",
          defaultClassNames.caption_label
        ),
        table: "w-full border-collapse",
        weekdays: cn("flex", defaultClassNames.weekdays),
        weekday: cn(
          "text-gray-300 flex-1 select-none rounded-full text-sm font-normal uppercase",
          defaultClassNames.weekday
        ),
        week: cn("mt-2 flex w-full", defaultClassNames.week),
        week_number_header: cn(
          "w-[--cell-size] select-none",
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          "text-muted-foreground select-none text-[0.8rem]",
          defaultClassNames.week_number
        ),
        day: cn(
          "rounded-full data-[selected=true]:bg-secondary data-[selected=true]:text-secondary-foreground data-[range-middle=true]:bg-accent data-[range-middle=true]:text-accent-foreground data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-ring/50 flex aspect-square h-auto w-full min-w-[--cell-size] flex-col gap-1 font-normal leading-none data-[range-end=true]:rounded-full data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-full group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px] [&>span]:text-xs [&>span]:opacity-70",
          defaultClassNames.day
        ),
        range_start: cn(
          "bg-accent rounded-l-md",
          defaultClassNames.range_start
        ),
        range_middle: cn("rounded-none", defaultClassNames.range_middle),
        range_end: cn("bg-accent rounded-r-md", defaultClassNames.range_end),
        today: cn(
          "bg-accent text-accent-foreground rounded-full",
          defaultClassNames.today
        ),
        outside: cn(
          "text-muted-foreground aria-selected:text-muted-foreground",
          defaultClassNames.outside
        ),
        disabled: cn(
          "text-muted-foreground opacity-50",
          defaultClassNames.disabled
        ),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          );
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return (
              <ChevronLeftIcon className={cn("size-4", className)} {...props} />
            );
          }

          if (orientation === "right") {
            return (
              <ChevronRightIcon
                className={cn("size-4", className)}
                {...props}
              />
            );
          }

          return (
            <ChevronDownIcon className={cn("size-4", className)} {...props} />
          );
        },
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-[--cell-size] items-center justify-center text-center">
                {children}
              </div>
            </td>
          );
        },
        ...components,
      }}
      {...props}
    />
  );
}

export { Calendar };
